import React from "react";
import { Helmet } from "react-helmet";
import { ShareSection } from "../../components/template";

// Hashtag section
const HashtagSection = ({ tags }) => (
  <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
    {tags.map((tag, index) => (
      <span
        key={index}
        className="bg-red-700 bg-opacity-10 text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 mb-2 hover:bg-red-700 hover:text-white"
      >
        {tag}
      </span>
    ))}
  </div>
);

const Blog15 = () => {
  const hashtags = [
    "#guthealth",
    "#mindbodyconnection",
    "#ayurvedalifestyle",
    "#healinghabits",
    "#ParamLife",
  ];

  return (
    <>
      <Helmet>
        <title>8 Everyday Triggers That Are Silently Destroying Your Gut</title>
        <meta
          name="description"
          content="8 everyday habits that silently weaken your gut health and how gentle awareness can restore balance, calm, and vitality."
        />
      </Helmet>

      <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed text-gray-800">
        <h1 className="text-secondary text-left text-3xl font-semibold mb-8">
          8 Everyday Triggers That Are Silently Destroying Your Gut
        </h1>
   <img
          src="/assets/knowyourself/blogs/blog15.png"
          alt="7 Things Our Grandparents Did Right (But We Ruined)"
          className="w-[80%] max-w-xl rounded-2xl shadow-md mx-auto mb-10 ml-10"
        />

        <section className="space-y-4 mb-8">
          <h2 className="text-secondary text-2xl font-semibold">
            Your Gut Is the Root of Your Well-being
          </h2>

          <p>
            In the ancient texts, it is said that when the stomach is at peace,
            the mind becomes still.
          </p>

          <p>
            Modern science calls it the “gut-brain connection,” but this wisdom
            is not new.
          </p>

          <p>
            Our ancestors understood that the way we eat, think, and live
            directly shapes the harmony within.
          </p>

          <p>
            Yet, in today’s pace of life, we have forgotten to listen.
            <br />
            We eat in a hurry. We carry worry into every bite.
            <br />
            And slowly, without realizing, we disturb the quiet intelligence of
            our gut — the very center of balance in the body.
          </p>

          <p className="text-[#578d48] font-semibold">
            The gut doesn’t fall sick overnight.
            <br />
            It whispers long before it breaks.
          </p>

          <p className="text-[#578d48] font-semibold">
            Here are eight everyday triggers that slowly weaken it — and how you
            can bring yourself back to balance with awareness and gentleness.
          </p>
        </section>

        <section className="space-y-6 mb-8">
          <div>
            <h3 className="text-primary font-semibold">1. Eating in a rush</h3>
            <p>
              The body digests not only food, but also the state in which the
              food is eaten.
            </p>
            <p>
              When you eat in a hurry — standing, scrolling through your phone,
              or rushing between meetings — the body stays in a state of
              tension.
            </p>
            <p>
              Your stomach cannot release the right enzymes when your mind is
              restless.
            </p>
            <p>
              A simple pause before eating — one deep breath, one moment of
              gratitude — can change everything.
            </p>
            <p className="text-[#578d48]">
              Let your body know it is safe to receive nourishment.
            </p>
            <p className="text-[#578d48] italic">
              “Food eaten in calmness becomes medicine.” <br />
              Food eaten in chaos becomes a burden.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              2. Overthinking and emotional strain
            </h3>
            <p>
              You may have noticed — whenever life feels heavy, the stomach
              feels it first.
            </p>
            <p>
              Anxiety tightens the gut. Worry disturbs digestion. Sadness slows
              metabolism.
            </p>
            <p className="text-[#578d48]">
              Your gut is not separate from your emotions; it is your second
              brain.
            </p>
            <p>
              If your mind is restless, your stomach will mirror it.
            </p>
            <p>
              Try to release tension through deep breathing, prayer, or sitting
              quietly after meals.
            </p>
            <p className="text-[#578d48]">
              When the mind calms, the gut heals.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">3. Late-night eating</h3>
            <p>
              The body works in rhythms — what Ayurveda calls dosha cycles.
            </p>
            <p>
              After sunset, the digestive fire naturally lowers.
            </p>
            <p>
              Eating late confuses this rhythm. Food remains undigested, leading
              to heaviness, bloating, and disturbed sleep.
            </p>
            <p>
              As much as possible, finish dinner before 8 p.m.
            </p>
            <p className="text-[#578d48]">
              Let your body rest through the night instead of digesting while
              you sleep.
            </p>
            <p className="text-[#578d48]">
              Healing happens best when the system is not overloaded.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              4. Cold and processed foods
            </h3>
            <p>
              Packaged snacks, chilled drinks, and reheated meals may be
              convenient, but they lack prana — the life force that fresh food
              carries.
            </p>
            <p className="text-[#578d48]">
              Cold food dampens digestion; processed food burdens it.
            </p>
            <p>
              Warm, freshly cooked meals — simple dal, rice, vegetables, soups
              — bring quiet strength to the gut.
            </p>
            <p>
              They are easy to digest, calming to the nerves, and gentle on the
              spirit.
            </p>
            <p className="text-[#578d48]">
              Your gut prefers simplicity over complexity.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">5. Ignoring thirst</h3>
            <p>
              Water is the silent healer of the body, yet we often replace it
              with tea, coffee, or sugary drinks.
            </p>
            <p>
              Dehydration slows down digestion and creates imbalance in gut
              bacteria.
            </p>
            <p>
              Keep a small copper or earthen pot nearby and sip water through
              the day.
            </p>
            <p>
              Avoid drinking too much during meals; instead, hydrate before and
              after.
            </p>
            <p className="text-[#578d48]">
              When water enters the body mindfully, it becomes not just
              hydration — but purification.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              6. Suppressing natural urges
            </h3>
            <p>
              In the rush of daily life, we often delay or ignore natural
              signals — hunger, thirst, sleep, or the call to use the restroom.
            </p>
            <p>
              Every time we suppress these rhythms, we send confusion to our
              system.
            </p>
            <p className="text-[#578d48]">
              Your body works best when it is allowed to flow in its own timing.
            </p>
            <p>
              Listen to it. Respect it. Respond to it.
            </p>
            <p className="text-[#578d48]">
              The gut thrives when you trust your body’s natural intelligence
              instead of forcing it into schedules.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              7. Too much worry about “perfect eating”
            </h3>
            <p>
              Ironically, the constant anxiety about eating right can also harm
              your gut.
            </p>
            <p>
              When food becomes a source of fear — “Is this healthy enough?”
              “Will this make me gain weight?” — your body digests stress, not
              nourishment.
            </p>
            <p className="text-[#578d48]">
              Eat with awareness, not obsession.
            </p>
            <p>
              Enjoy your meals. Be present.
            </p>
            <p className="text-[#578d48]">
              The energy with which you eat matters more than the ingredients
              on your plate.
            </p>
            <p className="text-[#578d48] italic">
              As the Bhagavad Gita says, “Purity of mind comes from purity of
              food” — and purity means calmness, not control.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              8. Lack of stillness after eating
            </h3>
            <p>
              Right after eating, many people rush to work, pick up their
              phones, or start chores.
            </p>
            <p className="text-[#578d48]">
              But digestion begins not in motion — it begins in rest.
            </p>
            <p>
              Try this instead: after meals, sit quietly for five minutes.
            </p>
            <p>
              Breathe softly, close your eyes, and maintain a straight spine.
            </p>
            <p className="text-[#578d48]">
              Allow your body to start converting food into energy, a sacred
              process.
            </p>
            <p className="text-[#578d48]">
              Stillness after food is not laziness — it is an act of
              thankfulness.
            </p>
          </div>
        </section>

        <section className="space-y-4 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">
            A Calm Final Note
          </h2>

          <p>
            Your stomach is a garden, not just a section of your body.
          </p>
          <p>
            It grows into vitality if you nourish it with calm, kindness, and
            care.
          </p>
          <p>
            It withers silently over time if you carelessly and carelessly
            ignore it.
          </p>
          <p>
            Growth comes from slowing down enough to care, not just from taking
            vitamins.
          </p>
          <p className="text-[#578d48]">
            Start today by taking a deep breath, eating a peaceful meal, and
            honoring your inner rhythm.
          </p>
          <p className="text-[#578d48]">
            Your gut will express gratitude in the form of calm, strength, and
            joy.
          </p>
          <p className="text-[#578d48]">
            Because the reality is straightforward: Your mind follows your
            stomach when it. Additionally, life becomes a little more
            Satvik—gentle, balanced, and full of silent joy—when the mind is at
            ease.
          </p>
        </section>

        <HashtagSection tags={hashtags} />
        <ShareSection />
      </article>
    </>
  );
};

export default Blog15;
